import { DotLegalCompletedPercentage, DotLegalEmptyState, DotLegalHeader } from "@dotlegal/dotlegal-ui-components";
import { Trans } from "react-i18next";
import { useTranslation } from "@localization/useTranslation";
import DotLegalLink from "@common/components/dotLegalLink/DotLegalLink";
import { useUrlProvider } from "../../useUrlProvider";
import Widget from "../Widget";
import WhiteDashboardBox from "../WhiteDashboardBox";
import { IRiskDashboardWidgetProps, useRiskDashboardWidget } from "./RiskDashboardWidget.hooks";
import RiskAppetiteBox from "./riskAppetiteBox/RiskAppetiteBox";
import RiskDashboardWidgetTopRisks from "./riskDashboardWidgetTopRisks/RiskDashboardWidgetTopRisks";
import NoRiskAreas from "../riskWidget/riskWidgetList/no-risk-templates.svg?react";
import { Box } from "@mui/material";

function RiskDashboardWidget(props: IRiskDashboardWidgetProps) {
    const { translateString } = useTranslation();
    const { getRiskAreasUrl } = useUrlProvider();
    const {
        isEnabled,
        isLoading,
        assessedPercentage,
        assessedEntities,
        totalEntities,
        risksOverAppetite,
        riskAppetiteLimit,
        isExceeded,
        topRisks,
        hasNoRiskAreas,
    } = useRiskDashboardWidget(props);

    return (
        <Widget disabled={!isEnabled} isPdf={props.isPdf}>
            <DotLegalHeader marginBottom={0} headerStyle={"medium"}>
                {translateString("risk").toUpperCase()}
            </DotLegalHeader>

            {hasNoRiskAreas ? (
                <WhiteDashboardBox marginTop={1} fullHeight>
                    <DotLegalEmptyState
                        textColor={"darkBlue"}
                        noFixedHeight
                        noPadding
                        icon={<NoRiskAreas />}
                        text={
                            <Trans
                                i18nKey={"dashboardNoRiskAreas"}
                                components={{
                                    riskAreas: (
                                        <DotLegalLink to={getRiskAreasUrl()} linkColor="primary">
                                            {undefined}
                                        </DotLegalLink>
                                    ),
                                }}
                            />
                        }
                    />
                </WhiteDashboardBox>
            ) : (
                <>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        <WhiteDashboardBox marginTop={1}>
                            <DotLegalHeader marginBottom={1} headerStyle={"small"}>
                                {translateString("riskManagementReportAssessedEntities")}
                            </DotLegalHeader>
                            <Box sx={(theme) => ({ fontSize: theme.typography.pxToRem(14) })}>
                                <DotLegalCompletedPercentage
                                    isLoading={isLoading}
                                    oneLiner
                                    completed={assessedPercentage}
                                    label={translateString("dashboardAssessedOfEntities", { assessed: assessedEntities, total: totalEntities })}
                                />
                            </Box>
                        </WhiteDashboardBox>

                        <RiskAppetiteBox
                            isLoading={isLoading}
                            riskAppetiteLimit={riskAppetiteLimit}
                            risksOverAppetite={risksOverAppetite}
                            isExceeded={isExceeded}
                        />
                    </Box>
                    <RiskDashboardWidgetTopRisks rows={topRisks} isLoading={isLoading} isPdf={props.isPdf} />
                </>
            )}
        </Widget>
    );
}

export default RiskDashboardWidget;
